<script setup lang="ts">
import { computed, inject, type Component } from "vue";
import { isJust, type Maybe } from "../../lib/maybe";
import { zwMenuKey } from "./zwMenuContext";

const props = withDefaults(
  defineProps<{
    label: string;
    /** Callers pass `just(text)` or `nothing` explicitly. */
    shortcut: Maybe<string>;
    disabled?: boolean;
    icon?: Component;
    title: Maybe<string>;
  }>(),
  {
    disabled: false,
  },
);

const emit = defineEmits<{
  select: [];
}>();

const menu = inject(zwMenuKey, null);

const itemTitle = computed(() => {
  if (isJust(props.title)) return props.title.value;
  if (isJust(props.shortcut)) {
    return `${props.label} (${props.shortcut.value})`;
  }
  return props.label;
});

function activate(event: Event): void {
  if (props.disabled) return;
  if (menu?.activateOn.value === "mousedown") {
    event.preventDefault();
  }
  emit("select");
  menu?.close();
}

function onClick(event: MouseEvent): void {
  if (menu?.activateOn.value !== "click") return;
  activate(event);
}

function onMouseDown(event: MouseEvent): void {
  if (menu?.activateOn.value !== "mousedown") return;
  activate(event);
}
</script>

<template>
  <button
    type="button"
    class="zw-menu-item"
    role="menuitem"
    :disabled="disabled"
    :title="itemTitle"
    @click="onClick"
    @mousedown="onMouseDown"
  >
    <span class="zw-menu-item-main">
      <component
        :is="icon"
        v-if="typeof icon !== 'undefined'"
        class="zw-menu-item-icon size-3.5"
        aria-hidden="true"
      />
      <span class="zw-menu-item-label">{{ label }}</span>
    </span>
    <kbd v-if="isJust(shortcut)" class="zw-menu-item-shortcut">{{
      shortcut.value
    }}</kbd>
  </button>
</template>

<style>
.zw-menu-item {
  appearance: none;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 0.4rem 0.45rem;
  border-radius: 0.25rem;
  font-family: var(--font-ui);
  font-size: 0.88rem;
  color: var(--text);
  cursor: pointer;
  text-align: left;
}

.zw-menu-item:hover:not(:disabled) {
  background: var(--hover-fill);
  color: var(--text-strong);
}

.zw-menu-item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.zw-menu-item:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 1px;
}

.zw-menu-item-main {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
}

.zw-menu-item-icon {
  flex-shrink: 0;
  color: var(--text-muted);
}

.zw-menu-item:hover:not(:disabled) .zw-menu-item-icon {
  color: var(--text-strong);
}

.zw-menu-item-label {
  min-width: 0;
}

.zw-menu-item-shortcut {
  flex-shrink: 0;
  font-family: var(--font-ui);
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: color-mix(in oklch, var(--text-muted) 80%, transparent);
}
</style>
